import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Badge from '@material-ui/core/Badge';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import withReducer from 'app/store/withReducer';

import { IoWarningOutline } from 'react-icons/io5';
import { MdOutlineError } from 'react-icons/md';
import { RiWifiOffLine } from 'react-icons/ri';
import { VscSync } from 'react-icons/vsc';

import { useNotifications } from 'app/admin/notification/NotificationProvider';
import { toggleNotificationPanel } from './store/stateSlice';
import reducer from './store';

function getStatusIcon(status) {
	const size = 12;

	switch (status) {
		case 'connecting':
			return <VscSync size={size} className="animate-spin" />;
		case 'error':
			return <MdOutlineError size={size} className="" />;
		case 'closed':
			return <RiWifiOffLine size={size} className="" />;
		case 'idle':
			return <IoWarningOutline size={size} className="" />;
		default:
			return null;
	}
}

const _safeInt = (v, fb = 0) => {
	const n = Number(v);
	return Number.isFinite(n) ? n : fb;
};

const _extractTotalCount = (resp) => {
	// régi: resp.metadata.totalCount
	// új/alternatív: resp.totalCount | resp.total | resp.data.totalCount | stb.
	if (!resp || typeof resp !== 'object') return 0;

	const v =
		resp?.metadata?.totalCount ??
		resp?.totalCount ??
		resp?.total ??
		resp?.data?.metadata?.totalCount ??
		resp?.data?.totalCount ??
		resp?.data?.total ??
		0;

	return _safeInt(v, 0);
};

function NotificationPanelToggleButton(props) {
	const [count, setCount] = useState(null);

	const dispatch = useDispatch();

	// belépés jel (nálatok: role tömb üres -> Guest)
	const isLoggedIn = useSelector(({ auth }) => !!auth?.user?.role?.length);

	const { update, status, getList } = useNotifications();

	const mountedRef = useRef(false);
	const pollRef = useRef(null);

	const POLL_MS = useMemo(() => 30000, []);

	const clearPoll = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	}, []);

	const poll = useCallback(() => {
		if (!isLoggedIn) {
			if (mountedRef.current) setCount(0);
			return Promise.resolve();
		}

		return getList()
			.then((resp) => {
				const c = _extractTotalCount(resp);
				if (mountedRef.current) setCount(c);
			})
			.catch(() => {
				if (mountedRef.current) setCount(0);
			});
	}, [getList, isLoggedIn]);

	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; };
	}, []);

	// update trigger: WS "card" -> update++, ilyenkor frissítsünk
	useEffect(() => {
		if (!isLoggedIn) { setCount(0); return; }
		poll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [update]);

	// status gate: csak OPEN-nél pollolunk időzítve (és azonnal egyszer)
	useEffect(() => {
		clearPoll();

		if (!isLoggedIn) {
			setCount(0);
			return;
		}

		if (status === 'open') {
			poll();

			pollRef.current = setInterval(() => {
				poll();
			}, POLL_MS);
		} else {
			// régi viselkedés: null-ra rakta; én inkább 0-t adok,
			// hogy a Badge ne kapjon null-t (de ha null kell, állítsd vissza).
			setCount(0);
		}

		return () => clearPoll();
	}, [status, isLoggedIn, poll, clearPoll, POLL_MS]);

	return (
		<IconButton
			className="w-40 h-40"
			onClick={(ev) => dispatch(toggleNotificationPanel())}
			disabled={!isLoggedIn}
		>
			<Badge color="secondary" max={99} badgeContent={count}>
				{props.children}
				{status !== 'open' && (
					<span className="absolute -bottom-4 -right-8 rounded-full bg-gray-200 opacity-90 p-3">
						{getStatusIcon(status)}
					</span>
				)}
			</Badge>
		</IconButton>
	);
}

NotificationPanelToggleButton.defaultProps = {
	children: <Icon>notifications</Icon>
};

export default withReducer('notificationPanel', reducer)(NotificationPanelToggleButton);
